'use client'

import * as React from 'react'
import { BookOpen, EllipsisVertical, Settings2 } from 'lucide-react'
import { motion } from 'motion/react'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ui/web/components'
import { LeaderboardIcon, MissionIcon, PuzzleIcon, ScoreboardIcon } from '@ui/web/icon'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { CardAction } from './card-action'
import type { LessonModuleCardSize } from './lesson-module-card'
import { lessonModuleCardSizeStyles } from './lesson-module-card'

export interface CourseMenuItem {
  label: string
  icon: React.ReactNode
  onClick: () => void
}

export const COURSE_MENU_ICONS = {
  peringkat: LeaderboardIcon,
  penilaian: ScoreboardIcon,
  misi: MissionIcon,
  overview: PuzzleIcon,
} as const

type ContinueLessonCardProps =
  | {
      variant?: 'lesson'
      id: string
      title: string
      coverUrl: string | null
      action: CardAction
      size?: LessonModuleCardSize
      className?: string
      progress?: number
      showProgress?: boolean
    }
  | {
      variant: 'course'
      id: string
      title: string
      coverUrl: string | null
      action: CardAction
      size?: LessonModuleCardSize
      className?: string
      menuItems?: CourseMenuItem[]
      onSettingsClick?: () => void
    }

export function ContinueLessonCard(props: ContinueLessonCardProps) {
  const { id, title, coverUrl, action, size = 'large', className } = props
  const { img } = lessonModuleCardSizeStyles[size]
  const fallbackIcon = size === 'large' ? 'srd:size-10' : 'srd:size-8'

  const imageInner = (
    <div
      className='srd:w-full srd:shrink-0 srd:bg-neutral-white srd:flex srd:items-center srd:justify-center srd:overflow-hidden'
      style={{ height: img.height }}
    >
      {coverUrl ? (
        <img
          src={coverUrl}
          alt={title}
          width={img.width}
          height={img.height}
          draggable={false}
          loading='lazy'
          decoding='async'
          className='srd:w-full srd:h-full srd:object-cover'
        />
      ) : (
        <BookOpen className={cn(fallbackIcon, 'srd:text-grey-40')} />
      )}
    </div>
  )

  if (props.variant === 'course') {
    const { menuItems, onSettingsClick } = props
    const hasMenu = (menuItems && menuItems.length > 0) || !!onSettingsClick

    const animatedImageInner = (
      <motion.div whileHover={{ scale: 1.025 }} whileTap={{ scale: 0.975 }} className='srd:w-full'>
        {imageInner}
      </motion.div>
    )

    const imageArea =
      action.type === 'link' ? (
        action.renderWrapper(animatedImageInner)
      ) : (
        <button type='button' onClick={action.onClick} className='srd:block srd:w-full'>
          {animatedImageInner}
        </button>
      )

    return (
      <div
        data-id={id}
        className={cn('srd:flex srd:flex-col srd:overflow-hidden srd:rounded-2xl srd:bg-neutral-white', className)}
        style={{ width: img.width }}
      >
        {imageArea}

        <div className='srd:flex srd:items-center srd:gap-1 srd:px-2 srd:py-2'>
          <span
            className={cn(
              typographyVariants({ variant: 'label-md' }),
              'srd:flex-1 srd:truncate srd:text-neutral-black',
            )}
          >
            {title}
          </span>

          {hasMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button aria-label='Menu tugas' variant='ghost' size='icon' className='srd:p-0 srd:w-fit srd:h-fit'>
                    <EllipsisVertical className='srd:size-6 srd:text-neutral-black' />
                  </Button>
                }
              />

              <DropdownMenuContent side='bottom' className='srd:w-full srd:max-w-[280px]' align='end'>
                {menuItems?.map((item, i) => (
                  <DropdownMenuItem key={i} onClick={item.onClick}>
                    {item.icon}
                    {item.label}
                  </DropdownMenuItem>
                ))}

                {onSettingsClick && (
                  <>
                    {menuItems && menuItems.length > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuItem onClick={onSettingsClick}>
                      <Settings2 />
                      Pengaturan tugas
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    )
  }

  // variant === 'lesson' (default) — image + optional progress bar + title below
  const { progress = 0, showProgress = true } = props
  const clampedProgress = Math.min(100, Math.max(0, progress))

  const content = (
    <motion.div
      data-id={id}
      whileHover={{ scale: 1.025 }}
      whileTap={{ scale: 0.975 }}
      className={cn(
        'srd:flex srd:flex-col srd:cursor-pointer srd:overflow-hidden srd:rounded-2xl srd:bg-neutral-white',
        className,
      )}
      style={{ width: img.width }}
    >
      {imageInner}

      {showProgress && (
        <div className='srd:w-full srd:h-2 srd:overflow-hidden srd:bg-grey-100/50'>
          <motion.div
            className='srd:h-full srd:bg-[#E73957]'
            initial={{ width: '0%' }}
            animate={{ width: `${clampedProgress}%` }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        </div>
      )}

      <span className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black srd:truncate srd:p-2')}>
        {title}
      </span>
    </motion.div>
  )

  if (action.type === 'link') return action.renderWrapper(content)

  return (
    <button type='button' onClick={action.onClick} className='srd:w-fit srd:text-left'>
      {content}
    </button>
  )
}
